import { config } from "@config/config";
import React, { PropsWithChildren, createContext, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  ApiReportEhrSyncChoices,
  ApiReportSectionsAiGeneratedStatusChoices,
  ApiReportTreatmentPlanAiStgPredictionStatusChoices,
  ApiReportTreatmentPlanAiTargetPredictionStatusChoices,
  ReportTypeEnums,
  useGetReportByIdQuery,
} from "@api/graphql/types-and-hooks";
import { useLDReportTemplateContext } from "@hooks/useLDReportTemplateContext";
import { getFilteredPageSectionsByLocation } from "@pages/AssessmentReportV2Details/Components/helper";
import { InitialAssessmentPageRoute } from "@pages/AssessmentReportV2Details/types";
import { ReportProvider } from "@providers/ReportProvider";

interface AssessmentBuilderDataI {
  isReviewPage: boolean;
}

const AssessmentBuilderContext = createContext<AssessmentBuilderDataI | undefined>(undefined);

export const useAssessmentBuilderDataOptional = () => {
  return useContext(AssessmentBuilderContext);
};

export const AssessmentBuilderProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentRoute =
    (pathname.split("#")?.pop()?.split("/").pop() as InitialAssessmentPageRoute) ||
    ("upload-files" as InitialAssessmentPageRoute);

  const isReviewAndSignPage = currentRoute === "review-and-sign";

  const assessmentQuery = useGetReportByIdQuery(
    {
      reportId: assessmentId || "<missing-assessment-id>",
      filter: {
        reportType: ReportTypeEnums.InitialAssessment,
      },
    },
    {
      enabled: !!assessmentId,
      queryKey: ["get-report-by-id", assessmentId, pathname],
      retry: false,
      refetchInterval: (query) => {
        const report = query?.state?.data?.getReports?.edges?.[0]?.node;
        if (!report) return false;
        const reportTreatmentPlan = query?.state?.data?.getReports?.edges?.map((edge) => edge?.node)?.[0]
          ?.reportTreatmentPlan;

        const stgStatus = reportTreatmentPlan?.aiStgPredictionStatus;
        const targetStatus = reportTreatmentPlan?.aiTargetPredictionStatus;
        const ehrSyncStatus = report?.ehrSync;

        // Check if EHR sync is in progress
        const isEhrSyncInProgress =
          ehrSyncStatus === ApiReportEhrSyncChoices.Pending || ehrSyncStatus === ApiReportEhrSyncChoices.Inprogress;

        const currentRoute =
          (pathname.split("#")?.pop()?.split("/").pop() as InitialAssessmentPageRoute) ||
          ("upload-files" as InitialAssessmentPageRoute);

        switch (currentRoute) {
          // Client Details, Upload Files, Review & Sign, and Prioritize will never require a re-fetch of the entire Report object
          case "client-details":
          case "upload-files":
          case "review-and-sign":
          case "prioritize":
            // But we still need to poll for EHR sync status
            return isEhrSyncInProgress ? config.REPORT_POLLING_INTERVAL : false;
          // If we're on either of the report writeup pages, and one of the pages has a section that is pending
          // we'll need to keep refetching until its status is complete
          case "report-part-1": {
            const sectionsForCurrentRoute = getFilteredPageSectionsByLocation(report, currentRoute);
            const someSectionsStillPredicting = sectionsForCurrentRoute.some(
              (s) => s.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending,
            );
            // TODO: BACKPORT: Need to resolve this when FDM has this field populate
            // const prioritizePageStillGenerating = report.reportTreatmentPlan?.prioritizeFieldsAiStatus
            if (someSectionsStillPredicting || isEhrSyncInProgress) {
              return config.REPORT_POLLING_INTERVAL;
            } else {
              return false;
            }
          }
          // If we're on either of the report writeup pages, and one of the pages has a section that is pending
          // we'll need to keep refetching until its status is complete
          case "report-part-2": {
            const sectionsForCurrentRoute = getFilteredPageSectionsByLocation(report, currentRoute);
            const someSectionsStillPredicting = sectionsForCurrentRoute.some(
              (s) => s.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending,
            );
            if (someSectionsStillPredicting || isEhrSyncInProgress) {
              return config.REPORT_POLLING_INTERVAL;
            } else {
              return false;
            }
          }
          // Short Term Goals page if short term goals or targets are still generating
          case "short-term-goals":
            if (
              stgStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.GoalPredictionPending ||
              targetStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionPending ||
              isEhrSyncInProgress
            ) {
              return config.REPORT_POLLING_INTERVAL;
            } else {
              return false;
            }
        }
      },
    },
  );

  const assessmentReport = assessmentQuery?.data?.getReports?.edges?.[0]?.node;

  useLDReportTemplateContext(assessmentReport?.reportTemplate);

  if (!assessmentQuery || assessmentQuery.isLoading) return null;

  if (assessmentReport && !assessmentReport.learner?.id && currentRoute !== "client-details")
    navigate(`client-details`);

  return (
    <AssessmentBuilderContext.Provider
      value={{
        isReviewPage: isReviewAndSignPage,
      }}
    >
      <ReportProvider report={assessmentReport ?? undefined} reportQuery={assessmentQuery} viewMode="step">
        {children}
      </ReportProvider>
    </AssessmentBuilderContext.Provider>
  );
};
